import React, { ReactNode } from 'react';

import Link from '@atlaskit/link';
import { MessageDescriptor, IntlShape } from 'react-intl';

import { LoadingState } from '@townsquare/loading-state';
import { fg } from '@townsquare/stat-sig/gate';

import { CreateCustomFieldButton } from '../components/SettingsHeader/CreateCustomFieldButton';
import { CreateTeamTypeButton } from '../components/SettingsHeader/CreateTeamTypeButton';
import { EditTeamTypeActionButton } from '../components/SettingsHeader/EditTeamTypeActionButton';
import { HRISActionButton } from '../components/SettingsHeader/HRISActionButton';

import { TEAM_TYPES_DOCUMENTATION_LINK } from './constants';
import { type ScreenType, ScreenTypes, DEFAULT_TEAM_SCREEN, DEFAULT_TEAM_SCREEN_NEXT } from './types';

export type BodyStackSpace = 'space.300' | 'space.500' | undefined;

type ScreenConfig = {
	pageTitle?: (intl: IntlShape['formatMessage']) => string;
	pageDescription?: (intl: IntlShape['formatMessage']) => ReactNode;
	parentScreen?: ScreenType;
	skeleton?: React.ReactNode;
	actionButton?: React.ReactNode;
	bodyStackSpace?: BodyStackSpace;
};

type ScreenFeature = keyof ScreenConfig;

// Some values need fallbacks to avoid needing to do fallbacks in place
type ScreenConfigWithDefaults = ScreenConfig &
	Required<Pick<ScreenConfig, 'skeleton' | 'pageTitle' | 'bodyStackSpace'>>;

const defaultScreenConfig: ScreenConfigWithDefaults = {
	pageTitle: () => '',
	pageDescription: () => '',
	skeleton: <LoadingState />,
	parentScreen: undefined,
	actionButton: null,
	bodyStackSpace: 'space.500',
};
export const screenConfig: Record<ScreenType, ScreenConfig> = {
	HRIS_CONNECTORS: {
		pageTitle: intl =>
			intl({
				id: 'townsquare.web.directory-header.hris-connections',
				description: 'HRIS connectors screen',
				defaultMessage: 'HRIS connections',
			}),
		actionButton: <HRISActionButton />,
	},
	TEAM_TYPES: {
		pageTitle: intl =>
			intl({
				id: 'townsquare.web.directory-header.team-types',
				description: 'Team types screen',
				defaultMessage: 'Team types',
			}),
		pageDescription: intl =>
			intl(
				{
					id: 'townsquare.web.directory-header.team-types-description',
					description: 'Team types screen description',
					defaultMessage:
						'Customize the name and description of your team types, or create new types to better model your organization’s team structure. <a>Understand team types</a>',
				},
				{
					a: chunks => <Link href={TEAM_TYPES_DOCUMENTATION_LINK}>{chunks}</Link>,
				},
			),
		actionButton: <CreateTeamTypeButton />,
	},
	TEAM_TYPES_CREATE: {
		pageTitle: (intl: (message: MessageDescriptor) => string) =>
			intl({
				id: 'townsquare.web.directory-header.team-types-create',
				description: 'Team types create screen',
				defaultMessage: 'Create team type',
			}),
		parentScreen: 'TEAM_TYPES',
		bodyStackSpace: undefined,
	},
	TEAM_TYPES_EDIT: {
		pageTitle: (intl: (message: MessageDescriptor) => string) =>
			intl({
				id: 'townsquare.web.directory-header.team-types-edit',
				description: 'Team types update screen',
				defaultMessage: 'Edit team type',
			}),
		parentScreen: 'TEAM_TYPES',
		actionButton: <EditTeamTypeActionButton />,
		bodyStackSpace: undefined,
	},
	CUSTOM_FIELDS: {
		pageTitle: intl =>
			intl({
				id: 'townsquare.web.directory-header.custom-fields',
				description: 'Team custom fields screen',
				defaultMessage: 'Fields',
			}),
		actionButton: <CreateCustomFieldButton />,
		bodyStackSpace: 'space.300',
	},
	NOTIFICATIONS: {
		pageTitle: intl =>
			intl({
				id: 'townsquare.web.directory-header.notifications',
				description: 'Team notifications settings screen',
				defaultMessage: 'Notifications settings',
			}),
		pageDescription: intl =>
			intl(
				{
					id: 'townsquare.web.directory-header.notifications-description',
					description: 'Team notifications settings screen description',
					defaultMessage:
						'Control when you receive email or in-app notifications from Teams. You can change these settings at any time. <a>Learn more about managing your notifications.</a>',
				},
				{
					a: chunks => (
						<Link href="https://community.atlassian.com/forums/Atlassian-Teams-User-Group/How-to-bulk-manage-Atlassian-Teams-notifications/ba-p/3180300">
							{chunks}
						</Link>
					),
				},
			),
	},
};

export function configForScreen<T extends ScreenFeature>(screen: ScreenType, feature: T): ScreenConfigWithDefaults[T] {
	const configMergedWithDefaults = {
		...defaultScreenConfig,
		...screenConfig[screen],
	};
	return configMergedWithDefaults[feature];
}

export const getScreenFromStringOrDefault = (screenKey: string | undefined): ScreenType =>
	screenKey && ScreenTypes.includes(screenKey as ScreenType)
		? (screenKey as ScreenType)
		: // If team types are enabled, default to it
			fg('enable_atlassian_team_types_m1')
			? DEFAULT_TEAM_SCREEN_NEXT
			: DEFAULT_TEAM_SCREEN;
